/**
 * buildView — NormalizedExtract → ViewModel (spec §5, §6). Pure: every data
 * decision (sections, labels, formats, empties, grouping, totals, checks,
 * focus keys) happens here; `now` is passed in.
 */
import { isPlainObject } from "./normalizeExtract";
import { humanize, normKey } from "./format/labels";
import { fmtMoney, isEmptyValue, isAddressLike } from "./format/registry";
import { displayDateTime } from "./format/dates";
import { buildNode, kvRow, legacyLabelFor } from "./shape/buildNode";
import { isScalarArray, isTableArray } from "./shape/buildTable";
import { parseFindings } from "./findings/parseFindings";
import { evalCondition, evalRules, getPath, renderTemplate } from "./checks/rules";
import { runBuiltins } from "./checks/builtins";
import { DEFAULT_CONFIGS } from "./configs";

const TONE_RANK = { crit: 0, warn: 1, ok: 2 };
const BULK_KEY = /^full_|_raw$|^raw_|workbook_(pairs|sheets)$/i;

const slug = (s) => String(s).replace(/[^A-Za-z0-9]+/g, "-").replace(/^-|-$/g, "").toLowerCase();
const lastKey = (path) => {
  const parts = String(path).match(/[^.[\]]+/g) || [];
  for (let i = parts.length - 1; i >= 0; i -= 1) if (!/^\d+$/.test(parts[i])) return parts[i];
  return path;
};
const parentKeyOf = (path) => {
  const parts = (String(path).match(/[^.[\]]+/g) || []).filter((p) => !/^\d+$/.test(p));
  return parts.length > 1 ? parts[parts.length - 2] : undefined;
};
const parentPath = (path) => String(path).replace(/(\.[^.[\]]+|\[\d+\])$/, "");

const isBulk = (key, v) =>
  BULK_KEY.test(key) ||
  (Array.isArray(v) && v.length > 500) ||
  (isPlainObject(v) && Object.keys(v).length > 200);

/** Parse "a.b[2].c" into [{key}, {index}] segments. */
const segmentsOf = (path) => {
  const out = [];
  String(path).replace(/([^.[\]]+)|\[(\d+)\]/g, (m, key, idx) => {
    if (idx !== undefined) out.push({ index: Number(idx) });
    else if (/^\d+$/.test(key)) out.push({ index: Number(key) });
    else out.push({ key });
    return m;
  });
  return out;
};

const kvStats = (blocks) => {
  const kvs = blocks.filter((b) => b.kind === "kv");
  return { total: kvs.length, found: kvs.filter((b) => !b.empty).length, empty: kvs.length - kvs.filter((b) => !b.empty).length };
};

/** Empty KV rows in a block list, including inside object groups. */
const countEmpty = (blocks) =>
  blocks.reduce(
    (n, b) => n + (b.kind === "kv" && b.empty ? 1 : b.kind === "object" ? countEmpty(b.blocks) : 0),
    0,
  );

export function buildView(norm, { configs = DEFAULT_CONFIGS, now = new Date(), options = {} } = {}) {
  const payload = norm.payload || {};
  const config =
    (norm.envelope?.schema_class && configs.bySchema[norm.envelope.schema_class]) ||
    (norm.route && configs.byRoute[norm.route]) ||
    null;
  const derived = config?.derive ? config.derive(payload, now) || {} : {};
  const twoLayer = isPlainObject(payload.extract);

  // ── build context ──
  const pathMeta = new Map();
  let currentSection = null;
  const ctx = {
    payload,
    now,
    consumed: new Set(),
    hidden: new Set(config?.hiddenPaths || []),
    disabled: new Set(config?.disableBuiltins || []),
    register: (path, label, key) => {
      if (!pathMeta.has(path)) pathMeta.set(path, { label, key, sectionId: currentSection });
    },
  };
  const tablesBySection = {};

  const resolveFieldPath = (path) => {
    if (getPath(payload, path) !== undefined) return path;
    const flat = path.replace(/(^|\.)extract\./, "$1");
    if (flat !== path && getPath(payload, flat) !== undefined) return flat;
    return path;
  };

  /** One config field spec → blocks. */
  const fieldBlocks = (f) => {
    const path = resolveFieldPath(f.path);
    if (ctx.consumed.has(path)) return [];
    const value = getPath(payload, path);
    if (f.hideWhenEmpty && isEmptyValue(value)) {
      ctx.consumed.add(path);
      return [];
    }
    let note;
    if (typeof f.note === "function") note = f.note(value, payload, derived, now);
    else if (f.note && (!f.note.when || evalCondition(f.note.when, { payload, derived, now }).ok)) {
      note = { text: renderTemplate(f.note.template, { payload, derived, now }), tone: f.note.tone };
    }
    const blocks = buildNode(lastKey(path), value, path, 1, ctx, {
      label: f.label,
      format: f.format,
      sensitive: f.sensitive,
      note,
      hideWhenEmpty: f.hideWhenEmpty,
      itemTone: f.itemTone,
      parentKey: parentKeyOf(path),
      siblings: getPath(payload, parentPath(path)),
    });
    ctx.consumed.add(path);
    if (f.breakdown && blocks[0]?.kind === "kv") {
      const items = f.breakdown(payload, derived) || [];
      if (items.length > 1) {
        blocks[0].breakdown = items.map((b) => ({ ...b, display: fmtMoney(b.value) }));
      }
    }
    return blocks;
  };

  const helpers = {
    group: (path, label, fields) => ({
      kind: "object",
      path,
      label,
      depth: 1,
      blocks: fields.flatMap(fieldBlocks),
      collapsedByDefault: false,
    }),
  };

  /** Children of an object value, as if they were top-level. */
  const childBlocks = (key, value, path) =>
    Object.entries(value).flatMap(([k, v]) =>
      buildNode(k, v, `${path}.${k}`, 1, ctx, { parentKey: key, siblings: value }),
    );

  const bulkBlocks = (key, value, path) => {
    if (isPlainObject(value)) {
      ctx.register(path, humanize(key), key);
      return [{ kind: "json", path, label: humanize(key), json: JSON.stringify(value, null, 2) }];
    }
    return buildNode(key, value, path, 1, ctx);
  };
  const entryCount = (v) => (Array.isArray(v) ? v.length : isPlainObject(v) ? Object.keys(v).length : 1);

  // ── details (envelope, `_` keys, detailPaths) ──
  const details = [];
  const env = norm.envelope || {};
  details.push({ label: "Schema", value: env.schema_class || "—" });
  details.push({
    label: "Extractor",
    value: env.extractor_name ? `${env.extractor_name}${env.extractor_version ? ` (${env.extractor_version})` : ""}` : "—",
  });
  details.push({ label: "Version", value: env.version ?? norm.indexVersion ?? "—" });
  details.push({ label: "Extracted", value: env.written_at ? displayDateTime(env.written_at) : "—" });
  details.push({ label: "Edited by", value: env.edited_by || "—" });
  details.push({ label: "File", value: (norm.blobName || "").split("/").pop() || "—" });
  details.push({ label: "Shape", value: norm.shape === "A" ? "Versioned envelope" : "Raw payload" });
  const underscore = (obj, base) =>
    Object.keys(obj || {})
      .filter((k) => k.startsWith("_"))
      .forEach((k) => {
        const p = base ? `${base}.${k}` : k;
        ctx.consumed.add(p);
        const v = obj[k];
        details.push({ label: humanize(k), value: isPlainObject(v) || Array.isArray(v) ? JSON.stringify(v) : String(v ?? "—") });
      });
  underscore(payload, "");
  if (twoLayer) underscore(payload.extract, "extract");
  (config?.detailPaths || []).forEach((p) => {
    const v = getPath(payload, p);
    ctx.consumed.add(p);
    if (v === undefined || String(p).startsWith("_")) return;
    details.push({ label: humanize(lastKey(p)), value: isPlainObject(v) || Array.isArray(v) ? JSON.stringify(v) : String(v ?? "—") });
  });

  // ── configured sections ──
  const sections = [];
  (config?.sections || []).forEach((spec) => {
    currentSection = spec.id;
    let blocks = [];
    let meta;
    let defaultCollapsed = spec.defaultCollapsed;
    if (spec.kind === "fields") {
      blocks = spec.fields.flatMap(fieldBlocks);
    } else if (spec.kind === "table") {
      const path = resolveFieldPath(spec.path);
      const arr = getPath(payload, path);
      if (isTableArray(arr) && !ctx.consumed.has(path)) {
        blocks = buildNode(lastKey(path), arr, path, 1, ctx, { label: spec.title, tableSpec: spec });
        const table = blocks.find((b) => b.kind === "table");
        if (table) {
          tablesBySection[spec.id] = table;
          meta = table.meta;
        }
      }
      ctx.consumed.add(path);
    } else if (spec.kind === "findings") {
      const text = getPath(payload, spec.path);
      const summary = spec.summaryPath ? getPath(payload, spec.summaryPath) : undefined;
      ctx.consumed.add(spec.path);
      if (spec.summaryPath) ctx.consumed.add(spec.summaryPath);
      const table = spec.refsFromTable ? tablesBySection[spec.refsFromTable] : null;
      const parsed = typeof text === "string" ? parseFindings(text, table?.rows) : null;
      ctx.register(spec.path, spec.title, lastKey(spec.path));
      if (parsed && parsed.findings.length >= 2) {
        blocks = [
          {
            kind: "findings",
            path: spec.path,
            label: spec.title,
            summary: typeof summary === "string" && summary.trim() ? summary.trim() : undefined,
            ...parsed,
          },
        ];
        meta = `${parsed.findings.length} findings`;
      } else {
        if (typeof summary === "string" && summary.trim()) {
          blocks.push({ kind: "prose", path: spec.summaryPath, label: "Summary", text: summary.trim(), clampLines: 3 });
        }
        if (typeof text === "string" && text.trim()) {
          blocks.push({ kind: "prose", path: spec.path, label: spec.title, text: text.trim(), clampLines: 4 });
        }
      }
    } else if (spec.kind === "prose") {
      const text = getPath(payload, spec.path);
      ctx.consumed.add(spec.path);
      if (typeof text === "string" && text.trim()) {
        ctx.register(spec.path, spec.title, lastKey(spec.path));
        blocks = [
          {
            kind: "prose",
            path: spec.path,
            label: spec.title,
            text: text.trim(),
            clampLines: 4,
            tone: spec.tone || (/^CRITICAL/i.test(text.trim()) ? "crit" : undefined),
          },
        ];
      }
    } else if (spec.kind === "auto") {
      const path = resolveFieldPath(spec.path);
      const v = getPath(payload, path);
      if (!isEmptyValue(v) && !ctx.consumed.has(path)) {
        blocks = isBulk(lastKey(path), v)
          ? bulkBlocks(lastKey(path), v, path)
          : isPlainObject(v) && !isAddressLike(v)
            ? childBlocks(lastKey(path), v, path)
            : buildNode(lastKey(path), v, path, 1, ctx);
        meta = `${entryCount(v)} entries`;
      }
      ctx.consumed.add(path);
    } else if (spec.kind === "custom") {
      blocks = spec.build(payload, helpers, { derived, now }) || [];
      (spec.consumes || []).forEach((p) => ctx.consumed.add(p));
    }
    if (blocks.length) sections.push({ id: spec.id, title: spec.title, blocks, meta, defaultCollapsed });
  });

  // ── remaining data (spec §7.3 / §7.4) ──
  const summaryId = config ? "other-fields" : "summary";
  const summaryBlocks = [];
  const extraSections = [];
  const reviewBlocks = [];

  const place = (key, value, path, { reviewLayer = false } = {}) => {
    if (ctx.consumed.has(path) || ctx.hidden.has(path) || key.startsWith("_")) return;
    if (isBulk(key, value)) {
      const id = `bulk-${slug(path)}`;
      currentSection = id;
      const blocks = bulkBlocks(key, value, path);
      if (blocks.length) {
        extraSections.push({ id, title: humanize(key), meta: `${entryCount(value)} entries`, blocks, defaultCollapsed: true });
      }
      return;
    }
    const scalarish =
      isEmptyValue(value) ||
      (!isPlainObject(value) && !Array.isArray(value) && !(typeof value === "string" && value.length >= 120)) ||
      isScalarArray(value) ||
      isAddressLike(value);
    if (scalarish) {
      currentSection = reviewLayer ? "review" : summaryId;
      const blocks = buildNode(key, value, path, 1, ctx);
      (reviewLayer ? reviewBlocks : summaryBlocks).push(...blocks);
      return;
    }
    const id = slug(path) || slug(key);
    currentSection = id;
    let blocks;
    if (isPlainObject(value)) {
      ctx.register(path, humanize(key), key);
      blocks = childBlocks(key, value, path);
    } else {
      blocks = buildNode(key, value, path, 1, ctx);
    }
    if (!blocks.length) return;
    const table = blocks.length === 1 && blocks[0].kind === "table" ? blocks[0] : null;
    extraSections.push({ id, title: humanize(key), blocks, meta: table?.meta });
  };

  if (twoLayer) {
    Object.entries(payload.extract).forEach(([k, v]) => place(k, v, `extract.${k}`));
    Object.entries(payload)
      .filter(([k]) => k !== "extract")
      .forEach(([k, v]) => place(k, v, k, { reviewLayer: true }));
  } else {
    Object.entries(payload).forEach(([k, v]) => place(k, v, k));
  }

  // Review leftovers join a configured "review" section, else lead the list.
  const reviewShown = reviewBlocks.filter((b) => !(b.kind === "kv" && b.empty));
  if (reviewBlocks.length) {
    const existing = sections.find((s) => s.id === "review");
    if (existing) existing.blocks.push(...reviewBlocks);
    else if (reviewShown.length || reviewBlocks.length) sections.unshift({ id: "review", title: "Review", blocks: reviewBlocks });
  }
  const tail = [];
  if (summaryBlocks.length) {
    tail.push({ id: summaryId, title: config ? "Other fields" : "Summary", blocks: summaryBlocks });
  }
  const allSections = [...sections, ...tail, ...extraSections];

  // ── checks ──
  const scope = { payload, derived, now };
  let checks = [];
  if (norm.rejection) {
    const r = norm.rejection;
    checks.push({
      id: "rejection",
      tone: "crit",
      title: "Extraction was rejected",
      detail: [r.reason, r.documentType ? `The extractor thinks this is a ${r.documentType}.` : null]
        .filter(Boolean)
        .join(" "),
    });
  }
  checks.push(...evalRules(config?.rules, scope));
  if (config?.checks) checks.push(...(config.checks(payload, derived, now) || []));
  checks.push(...runBuiltins(payload, { now, disabled: config?.disableBuiltins }));
  if (!ctx.disabled.has("total-mismatch")) {
    allSections.forEach((s) =>
      s.blocks
        .filter((b) => b.kind === "table" && b.total && b.total.matches === false)
        .forEach((b) =>
          checks.push({
            id: `total-mismatch:${b.path}`,
            tone: "crit",
            title: `${b.label} total ${b.total.display}, stated total is ${b.total.expectedDisplay}`,
            target: { sectionId: s.id },
          }),
        ),
    );
  }
  const seen = new Set();
  checks = checks
    .filter((c) => (seen.has(c.id) ? false : seen.add(c.id)))
    .map((c, i) => ({ ...c, _i: i }))
    .sort((a, b) => TONE_RANK[a.tone] - TONE_RANK[b.tone] || a._i - b._i)
    .map(({ _i, ...c }) => c);

  // Fill a target's section from the path it points at.
  const sectionForPath = (path) => {
    let p = path;
    while (p) {
      const m = pathMeta.get(p);
      if (m?.sectionId) return m.sectionId;
      const next = parentPath(p);
      if (next === p) break;
      p = next;
    }
    return undefined;
  };
  checks.forEach((c) => {
    if (c.target && !c.target.sectionId && c.target.path) {
      const sid = sectionForPath(c.target.path);
      if (sid) c.target = { ...c.target, sectionId: sid };
      else delete c.target;
    }
  });

  // ── badges + meta ──
  allSections.forEach((s) => {
    const table = s.blocks.find((b) => b.kind === "table");
    const spec = (config?.sections || []).find((x) => x.id === s.id);
    const rowsFlag = table && spec?.flagRowsWithoutValue ? table.counts.withoutValue : 0;
    const findings = s.blocks
      .filter((b) => b.kind === "findings")
      .reduce((n, b) => n + b.findings.filter((f) => f.tone !== "ok").length, 0);
    const checkCount = checks.filter(
      (c) => c.tone !== "ok" && c.target?.sectionId === s.id && !(rowsFlag && c.target.expandGroup),
    ).length;
    s.badge = rowsFlag + findings + checkCount || undefined;
    const st = kvStats(s.blocks);
    s.emptyCount = countEmpty(s.blocks);
    if (!s.meta && st.total > 1 && st.total === s.blocks.length) s.meta = `${st.found} of ${st.total} found`;
  });

  // ── focus index (spec §14) ──
  const filePrefix = norm.shape === "A" ? "payload." : "";
  const focusIndex = [];
  pathMeta.forEach((m, path) => {
    if (!m.sectionId) return;
    const legacy = legacyLabelFor(segmentsOf(`${filePrefix}${path}`));
    focusIndex.push({
      path,
      sectionId: m.sectionId,
      keys: [normKey(path), normKey(m.label), normKey(legacy), normKey(m.key)].filter(Boolean),
    });
  });
  allSections.forEach((s) => focusIndex.push({ path: null, sectionId: s.id, keys: [normKey(s.title)] }));

  const hasContent = allSections.some((s) =>
    s.blocks.some((b) => !(b.kind === "kv" && b.empty)),
  );

  return {
    title: config?.title || norm.docType || null,
    configId: config?.id || null,
    checks,
    sections: allSections,
    details,
    canShowInText: !!norm.hasMarkdown,
    focusIndex,
    isEmpty: !hasContent,
    options,
  };
}

/** Resolve a `focusField` string to a focus entry (spec §14). */
export const resolveFocus = (focusIndex, focusField) => {
  const f = normKey(focusField);
  if (!f) return null;
  const exact = focusIndex.find((e) => e.keys.includes(f));
  if (exact) return exact;
  const suffix = focusIndex.find((e) => e.keys.some((k) => k.endsWith(` ${f}`)));
  if (suffix) return suffix;
  return focusIndex.find((e) => e.keys.some((k) => k.includes(f))) || null;
};

export { kvRow };
export default buildView;
